import React, { Component, Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';
import IconButton from '@mui/material/IconButton';
import TextField from '../../components/form/TextField';
import ColumnLayout from '../../components/layout/ColumnLayout';
import Grid from '../../components/layout/Grid';
import Inline, { inlineAlignment } from '../../components/layout/Inline';
import EditIcon from '../../components/icon/EditIcon';
import DeleteIcon from '../../components/icon/DeleteIcon';
import CremationOrderFragment from '../../fragments/CremationOrder';
import AddressAutocomplete, { createAddressComponentFields } from '../../components/form/AddressAutocomplete';
import Button from '../../components/form/Button';
import AlertModal from '../../components/modal/AlertModal';
import { numberToWords } from '../../utils/strings';
import Checkbox from '../../components/form/Checkbox';
import ContactLookup from '../../components/contact/ContactLookup';
import ContactCard from '../../components/contact/ContactCard';
import { isContactDefined } from '../../utils/bookable';
import ReadonlyField from '../../components/form/ReadonlyField';
import { TickCircleIcon } from '../../components/IconIndex';
import { validationHelper } from '../../utils/validation';

const clientPostalComponentFields = createAddressComponentFields('Client.PostalAddress');
const clientResidentialComponentFields = createAddressComponentFields('Client.ResidentialAddress');

class ContactDetailsTab extends Component {
    state = {
        readonlyClientDetails: true,
        readonlyBusinessDetails: true,
        editAuthorisedContactIndex: null,
        showDeleteAuthorisedContactAlertModal: false,
        authorisedContactToDelete: null
    };

    static getDerivedStateFromProps(newProps, oldState) {
        const { form } = newProps;
        const { currentRecordID } = oldState;
        if (!currentRecordID && form) {
            const setRecordID = form.getField('ID');
            const funeralID = form.getField('Funeral.ID');
            return ({
                currentRecordID: setRecordID,
                readonlyClientDetails: !!funeralID,
                readonlyBusinessDetails: !!funeralID
            });
        }
        return null;
    }

    render() {
        const { showDeleteAuthorisedContactAlertModal } = this.state;

        return (
            <Fragment>
                <ColumnLayout>
                    {this.renderLeftColumn()}
                    {this.renderRightColumn()}
                </ColumnLayout>

                <AlertModal
                    variant="warning"
                    open={showDeleteAuthorisedContactAlertModal}
                    title="Prompt"
                    secondaryAction="Yes, Delete"
                    onClickSecondaryAction={() => this.handleDeleteAuthorisedContact()}
                    primaryAction="Cancel"
                    onClickPrimaryAction={() => this.handleCloseDeleteAuthorisedContactModal()}
                    onClose={() => this.handleCloseDeleteAuthorisedContactModal()}
                >
                    <p>You're about to delete this authorised contact.</p>
                    <p>Are you sure?</p>
                </AlertModal>
            </Fragment>
        );
    }

    renderLeftColumn() {
        return (
            <Fragment>
                {this.renderClientDetails()}
            </Fragment>
        );
    }

    renderClientDetails() {
        const { form } = this.props;
        const { readonlyClientDetails } = this.state;
        const funeralID = form.getField('Funeral.ID');

        return (
            <Grid container bucket>
                <Grid item>
                    <Inline alignment={inlineAlignment.rightAlignSiblings}>
                        <h4>Applicant Details</h4>

                        <Checkbox label="Same as Funeral Director" form={form} name="EnquirerWasBusiness" />

                        {!!funeralID && <IconButton
                            className="icon"
                            title={readonlyClientDetails ? 'Edit' : 'Accept'}
                            onClick={() => this.setState({ readonlyClientDetails: !readonlyClientDetails })} size="large">
                            {readonlyClientDetails ? <EditIcon /> : <TickCircleIcon />}
                        </IconButton>}
                    </Inline>
                </Grid>

                {!form.getField('EnquirerWasBusiness') && (
                    <Fragment>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="First name"
                                form={form}
                                name="Client.FirstName"
                                readOnly={readonlyClientDetails}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField label="Surname" form={form} name="Client.Surname" readOnly={readonlyClientDetails} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Relationship to deceased"
                                form={form}
                                name="Client.RelationshipToDeceased"
                                readOnly={readonlyClientDetails}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField label="Phone" form={form} name="Client.PrimaryNumber" readOnly={readonlyClientDetails} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Mobile"
                                form={form}
                                name="Client.SecondaryNumber"
                                readOnly={readonlyClientDetails}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField label="Email" form={form} name="Client.Email" readOnly={readonlyClientDetails} />
                        </Grid>

                        <Grid item xs={12}>
                            <AddressAutocomplete
                                allowCustomAddress
                                label="Residential Address"
                                placeholder="Search for residential address..."
                                form={form}
                                componentFields={clientResidentialComponentFields}
                                readOnly={readonlyClientDetails}
                            />
                        </Grid>

                        {(!readonlyClientDetails || !!form.getField('Client.PostalAddressSameAsResidentialAddress')) && (
                            <Grid item xs={12}>
                                <Checkbox
                                    form={form}
                                    name="Client.PostalAddressSameAsResidentialAddress"
                                    label="Postal Address same as Residential Address"
                                    readOnly={readonlyClientDetails}
                                />
                            </Grid>
                        )}

                        {!form.getField('Client.PostalAddressSameAsResidentialAddress') && (
                            <Grid item xs={12}>
                                <AddressAutocomplete
                                    allowCustomAddress
                                    label="Postal Address"
                                    placeholder="Search for postal address..."
                                    form={form}
                                    name="Client.PostalAddress"
                                    componentFields={clientPostalComponentFields}
                                    readOnly={readonlyClientDetails}
                                />
                            </Grid>
                        )}
                    </Fragment>
                )}
            </Grid>
        );
    }

    renderRightColumn() {
        return <Fragment>{this.renderFuneralDirector()}{this.renderAuthorisedContacts()}</Fragment>;
    }

    renderAuthorisedContacts() {
        const { form } = this.props;
        const authorisedContacts = form.getState('AuthorisedContacts') || [];
        return (
            <Grid container bucket>
                <Grid item>
                    <h4>Authorised Contacts</h4>
                </Grid>

                {authorisedContacts.map((authorisedContact, index) =>
                    this.renderAuthorisedContact(authorisedContact, index)
                )}

                <Grid item>
                    <Button variant="primary" onClick={() => this.handleAddAuthorisedContact()}>
                        + Add Authorised Contact{' '}
                    </Button>
                </Grid>
            </Grid>
        );
    }

    renderAuthorisedContact(authorisedContact, index) {
        const { form } = this.props;
        const { editAuthorisedContactIndex } = this.state;
        const acField = 'AuthorisedContacts[' + index + ']';
        const authorisedContactPostalAddressComponentFields = createAddressComponentFields(
            acField + '.PostalAddress'
        );
        const authorisedContactResidentialAddressComponentFields = createAddressComponentFields(
            acField + '.ResidentialAddress'
        );
        const isReadOnly = editAuthorisedContactIndex !== index;
        return (
            <Grid key={index} itemBucket>
                <Grid variant="justify" item>
                    <Inline alignment={inlineAlignment.rightAlignSiblings} center>
                        <h5>Authorised Contact - {numberToWords(index + 1)}</h5>

                        <IconButton
                            className="icon"
                            title={isReadOnly ? 'Edit' : 'Accept'}
                            onClick={() => this.handleToggleEditAuthorisedContactModal(index)} size="large">
                            {isReadOnly ? <EditIcon /> : <TickCircleIcon />}
                        </IconButton>

                        <IconButton
                            className="icon"
                            title={'Delete'}
                            onClick={() => this.handleShowDeleteAuthorisedContactModal(authorisedContact)} size="large">
                            <DeleteIcon />
                        </IconButton>
                    </Inline>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="First name"
                        form={form}
                        name={acField + `.FirstName`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Surname"
                        form={form}
                        name={acField + `.Surname`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Relationship to deceased"
                        form={form}
                        name={acField + `.RelationshipToDeceased`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Phone"
                        form={form}
                        name={acField + `.PrimaryNumber`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Mobile"
                        form={form}
                        name={acField + `.SecondaryNumber`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Email"
                        form={form}
                        name={acField + `.Email`}
                        readOnly={isReadOnly}
                    />
                </Grid>

                <Grid item xs={12}>
                    <AddressAutocomplete
                        allowCustomAddress
                        label="Residential Address"
                        placeholder="Search for residential address..."
                        form={form}
                        componentFields={authorisedContactResidentialAddressComponentFields}
                        readOnly={isReadOnly}
                    />
                </Grid>


                {(!isReadOnly || !!form.getField(acField + '.PostalAddressSameAsResidentialAddress')) && (
                    <Grid item xs={12}>
                        <Checkbox
                            form={form}
                            name={`AuthorisedContacts[${index}].PostalAddressSameAsResidentialAddress`}
                            label="Postal Address same as Residential Address"
                            readOnly={isReadOnly}
                        />
                    </Grid>
                )}

                {!form.getField(acField + '.PostalAddressSameAsResidentialAddress') && (
                    <Grid item xs={12}>
                        <AddressAutocomplete
                            allowCustomAddress
                            label="Postal Address"
                            placeholder="Search for postal address..."
                            form={form}
                            name={acField + '.PostalAddress'}
                            componentFields={authorisedContactPostalAddressComponentFields}
                            readOnly={isReadOnly}
                        />
                    </Grid>
                )}

                <Grid item>
                    <TextField
                        label="Notes"
                        name={`AuthorisedContacts[${index}].Notes`}
                        form={form}
                        multiline
                        readOnly={editAuthorisedContactIndex !== index}
                    />
                </Grid>
            </Grid>
        );
    }

    renderFuneralDirector = () => {
        const { form } = this.props;
        const funeralID = form.getField('Funeral.ID');
        const businessContact = form.getField('Business');
        const { readonlyBusinessDetails } = this.state;
        return (
            <Grid container bucket>
                <Grid item>
                    <Inline alignment={inlineAlignment.rightAlignSiblings}>
                        <h4>Funeral Director</h4>
                        {!!funeralID && <IconButton
                            className="icon"
                            title={readonlyBusinessDetails ? 'Edit' : 'Accept'}
                            onClick={() => this.setState({ readonlyBusinessDetails: !readonlyBusinessDetails })} size="large">
                            {readonlyBusinessDetails ? <EditIcon /> : <TickCircleIcon />}
                        </IconButton>}
                    </Inline>
                </Grid>

                {(!businessContact && (!readonlyBusinessDetails ? (
                    <Grid item>
                        <ContactLookup
                            name="Business.ID"
                            form={form}
                            onSelect={(_, addressBook) =>
                                this.handleSelectBusinessContact('Business', addressBook)
                            }
                            onAddNewContact={addressBook =>
                                this.handleSelectBusinessContact('Business', addressBook)
                            }
                        />
                    </Grid>
                ) : (<Grid item><ReadonlyField>None</ReadonlyField></Grid>))) || (
                    <Grid item>
                        {!readonlyBusinessDetails && (
                            <div style={{ float: 'right', marginTop: -24 }}>
                                <Button variant="link-orange" onClick={() => form.setField({ Business: null })}>
                                    Change Contact
                                </Button>
                            </div>
                        )}
                        <div className="container">
                            <ContactCard
                                variant="fullwidth" //Do no remove
                                contact={businessContact}
                            />
                        </div>
                    </Grid>
                )}
                <Grid item md={6}>
                    <TextField
                        form={form}
                        name="DeliveredBy"
                        label="Funeral Coordinator"
                        placeholder="Enter name of Funeral Coordinator..."
                        readOnly={readonlyBusinessDetails}
                    />
                </Grid>
                <Grid item md={6}>
                    <TextField
                        form={form}
                        name="Recipient"
                        label="Funeral Conductor"
                        placeholder="Enter name of Funeral Conductor..."
                        readOnly={readonlyBusinessDetails}
                    />
                </Grid>
            </Grid>
        );
    };

    handleAddAuthorisedContact() {
        const { form } = this.props;
        const AuthorisedContacts = form.getState('AuthorisedContacts') || [];
        AuthorisedContacts.push({});
        form.setState({ AuthorisedContacts });
        this.handleToggleEditAuthorisedContactModal(AuthorisedContacts.length - 1);
    }

    handleToggleEditAuthorisedContactModal(index) {
        if (this.state.editAuthorisedContactIndex !== index) {
            this.setState({ editAuthorisedContactIndex: index });
        } else {
            this.setState({ editAuthorisedContactIndex: null });
        }
    }

    handleShowDeleteAuthorisedContactModal(authorisedContact) {
        this.setState({ authorisedContactToDelete: authorisedContact });
        this.setState({ showDeleteAuthorisedContactAlertModal: true });
    }

    handleCloseDeleteAuthorisedContactModal() {
        this.setState({ authorisedContactToDelete: null });
        this.setState({ showDeleteAuthorisedContactAlertModal: false });
    }

    handleDeleteAuthorisedContact() {
        const { form } = this.props;
        const AuthorisedContacts = form.getState('AuthorisedContacts') || [];

        let authorisedContact = this.state.authorisedContactToDelete;

        let authorisedContactIndex = AuthorisedContacts.indexOf(authorisedContact);
        if (authorisedContactIndex !== -1) {
            AuthorisedContacts.splice(authorisedContactIndex, 1);
            form.setState({ AuthorisedContacts });
        }

        this.setState({ authorisedContactToDelete: null });
        this.setState({ showDeleteAuthorisedContactAlertModal: false });
    }

    handleSelectBusinessContact(propertyName, addressBook) {
        const { form } = this.props;
        delete addressBook['__typename'];

        if (!!addressBook) {
            form.setState({ [propertyName]: addressBook });
        }
    }
}

export default {
    id: 'ContactDetails',
    label: 'Contact Details',
    component: withStyles({})(ContactDetailsTab),
    fragment: CremationOrderFragment,
    validation: {
        onValidate: {
            'Client.FirstName': (newValue, persistedValue, hasValue, getField) => {
                if (!getField('EnquirerWasBusiness')) {
                    if ('' === newValue || null === newValue || (undefined === newValue && !persistedValue)) {
                        return validationHelper.required();
                    }
                }
                return validationHelper.ok();
            }
        }
    },
    formatSaveData: (saveData, state) => {
        if (isContactDefined(saveData.Business)) {
            saveData.BusinessID = state.Business.ID;
            delete saveData.Business;
        } else if (saveData.Business === null) {
            saveData.BusinessID = null;
            delete saveData.Business;
        }
    }
};
